// src/lib/finance-row-filters.js
// Predicates for the finance "refund-family" save/total filters.
// Extracted from inline logic in src/App.jsx (CP/payments save filter) and
// src/pages/GenBalances/index.jsx (Total Wallets sum) so they can be unit-tested with
// real fail-before/pass-after coverage — same precedent as src/lib/merge-by-id.js.
//
// WHY (refund-family audit, 2026-06-04):
//   Negative amounts are LEGITIMATE — they are customer/brand refunds (e.g. Rose's
//   #212 −$650, TRADETRADITION −$1322). Several filters used `> 0` / `<= 0`, which
//   silently dropped or excluded those refunds BEFORE they reached the (already-fixed)
//   backend. These helpers fix the predicate at the source and pin the behavior.

import { serverNow } from './server-clock.js';

/**
 * Should a payments / customer-payments row be KEPT on the client save path?
 *
 * Old bug (App.jsx site a): `if (amt > 0) return true; else keep only if $0 && age<30min`.
 * That routed EVERY negative amount into the "$0" branch and dropped it once older than
 * 30 minutes → legit refunds vanished before POST, so the backend `amt !== 0` fix never
 * saw them.
 *
 * New rule:
 *   • any non-zero amount (incl. negative refunds) → always keep;
 *   • amount === 0 with a non-blank invoice → keep (a real, in-progress entry);
 *   • amount === 0 AND blank invoice → keep only if the row is younger than `graceMs`
 *     (being typed right now); otherwise it is stale junk → drop.
 *
 * @param {object} r            row with { amount, invoice, createdAt, updatedAt }
 * @param {number} [now]        current epoch ms (injectable for deterministic tests)
 * @param {number} [graceMs]    how long a fresh $0/blank row is kept (default 30 min)
 * @returns {boolean} true → keep, false → drop
 */
export function keepPaymentRow(r, now = serverNow(), graceMs = 30 * 60 * 1000) {
  const amt = parseFloat((r && r.amount) || 0);
  if (!Number.isNaN(amt) && amt !== 0) return true; // non-zero (negatives too) — always keep
  const inv = String((r && r.invoice) || '').trim();
  if (inv.length > 0) return true; // $0 but has an invoice — a real entry, keep
  const age = now - ((r && (r.createdAt || r.updatedAt)) || 0); // record clock
  return age < graceMs; // $0 + blank: fresh → keep (being edited); stale → drop
}

/**
 * Parse a GenBalances wallet `balance` display string to a signed Number, or NaN if blank.
 *
 * The balance field is a display string (may carry "$", commas, etc.); we strip to digits,
 * a single dot and a minus sign, then parseFloat. A blank/non-numeric balance → NaN
 * (caller treats it as "no number → skip").
 *
 * @param {string|number} balance
 * @returns {number} signed amount, or NaN when there is no number
 */
export function parseWalletBalance(balance) {
  const raw = String(balance == null ? '' : balance).replace(/[^0-9.-]/g, '');
  if (raw === '') return NaN;
  return parseFloat(raw);
}

/**
 * Should a wallet row's balance be INCLUDED in the Total Wallets sum?
 *
 * Old bug (GenBalances site c): `if (bal <= 0) return;` skipped negative balances entirely
 * instead of subtracting them → overstated Total Wallets → that inflated total flowed into
 * the saved daily-summary profit report (todayBal / monthlyP / dailyP) and the midnight shift.
 *
 * New rule: include the balance whenever it is a real number (including 0 and negatives);
 * skip only blank / non-numeric balances (unchecked wallets). The sign then flows through
 * the sum naturally.
 *
 * @param {number} parsedBalance  output of parseWalletBalance()
 * @returns {boolean} true → add to total, false → skip
 */
export function includeWalletBalance(parsedBalance) {
  return typeof parsedBalance === 'number' && !Number.isNaN(parsedBalance);
}

/**
 * Is the amount entered in the Affiliate-Payments PaymentForm valid to submit?
 *
 * Old bug (Dashboard/index.jsx site, payments-form twin of CP site b):
 *   `!f.amount || parseFloat(f.amount) <= 0` — rejected blank AND any value ≤ 0. A Brand
 *   Refund on the payments table is a NEGATIVE amount, so this validation BLOCKED creating or
 *   editing every brand-refund row from the modal (same money-correctness class as the CP form
 *   and the server-side `> 0` filter). The user could not enter the refund at all.
 *
 * New rule: an amount is valid when it is present AND parses to a finite, NON-ZERO number.
 *   • negative refunds (e.g. -650) → valid;
 *   • positive payments → valid;
 *   • blank / non-numeric / exactly 0 → invalid (there is no such thing as a $0 payment row).
 *
 * @param {string|number} amount  raw value from the form field (string from a number input)
 * @returns {boolean} true → allow submit, false → block with "Amount is required"
 */
export function paymentFormAmountValid(amount) {
  if (amount == null || String(amount).trim() === '') return false; // blank → invalid
  const n = parseFloat(amount);
  return Number.isFinite(n) && n !== 0; // finite & non-zero (negatives allowed)
}

/**
 * На сколько сдвинуть счётчик `ftd` связанной CRG-сделки после правки FTD-записи.
 *
 * WHY (Tier 3 review 2026-05-14, в main дожило до 2026-08-08):
 *   FtdsInfo::syncFtdToCrg добавлял к crg.ftd ПОЛНОЕ новое значение, не вычитая то,
 *   что уже добавил на предыдущем нажатии клавиши. Каждый ввод в ячейку — отдельный
 *   вызов, поэтому набор «1 → 2 → 3» накручивал 1+2+3 = 6 вместо 3. crg.ftd умножается
 *   на цену сделки, то есть завышение уходило прямо в выручку.
 *
 *   Дельта вычислима, потому что updateField (FtdsInfo/index.jsx:332) зовёт синк внутри
 *   `setFtdEntries(prev => ...)` и передаёт `prev.find(...)` — запись ДО правки.
 *
 *   Отрицательная дельта штатна и нужна: поправили 5 на 3 — счётчик обязан опуститься,
 *   иначе завышение остаётся навсегда. Очистка поля снимает ранее добавленное целиком.
 *
 * parseInt (не parseFloat) — поле FTD целочисленное, тем же способом читает вызывающий.
 *
 * @param {string|number|null|undefined} oldEntryFtd  значение поля ДО правки
 * @param {string|number|null|undefined} newEntryFtd  значение поля ПОСЛЕ правки
 * @returns {number} сдвиг счётчика; 0 → менять нечего
 */
export function ftdSyncDelta(oldEntryFtd, newEntryFtd) {
  const before = parseInt(oldEntryFtd, 10) || 0;
  const after  = parseInt(newEntryFtd, 10) || 0;
  return after - before;
}

/** 'YYYY-MM-DD' | 'DD/MM/YYYY' → 'YYYY-MM-DD'; иначе null. Оба формата реально живут в
 *  архиве: reportIsReady пишет ISO, updateDS — DD/MM/YYYY. */
function _isoDay(raw) {
  const s = String(raw ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

/**
 * Баланс на конец ПРЕДЫДУЩЕГО дня для карточки архива Daily Summary.
 *
 * WHY (сверка истории 2026-08-08): поле `yesterday`, записанное в снимок, неверно во
 * ВСЕХ 81 переходе архива — медиана ошибки $65 160, максимум $109 863. Его писал
 * перенос дня, чей результат затирался ответом сервера (починено #854/#855), поэтому
 * значение стояло сериями по 28, 11, 10 дней — 12 серий на 82 дня, и всего 10
 * различных чисел: два значения повторяются несмежными сериями.
 *
 * Историю при этом переписывать НЕ нужно: правильный ответ уже лежит в данных —
 * `_todayBal` каждого снимка посчитан кнопкой «Report is Ready» по реальной сумме
 * кошельков. Читаем его, а протухшее поле игнорируем.
 *
 * null (а не 0) когда предыдущего КАЛЕНДАРНОГО дня в архиве нет — таких дней 23 из 81
 * (выходные, дни без нажатия кнопки). Вызывающий отбрасывает плашку по `val != null`,
 * и она просто не рисуется: показать баланс позапрошлой недели под подписью
 * «Yesterday» хуже, чем не показать ничего, а ноль читался бы как «баланс был нулевой».
 *
 * Ищем по ДАТЕ, а не по соседству в массиве: порядок вызывающий меняет сортировкой,
 * плюс в архиве встречаются дубли по дате (5 из 87) — из них берём поздний archivedAt,
 * ту же запись показывает и сама карточка.
 *
 * @param {Array<object>} archive  весь dsArchive
 * @param {object} snap            снимок, для которого рисуется плашка
 * @returns {number|null} баланс на конец предыдущего дня либо null
 */
export function archivedYesterdayBalance(archive, snap) {
  if (!Array.isArray(archive) || !snap) return null;
  const key = _isoDay(snap.date);
  if (!key) return null;

  const d = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() - 1);           // UTC: сдвиг дня не должен зависеть от пояса
  const prevKey = d.toISOString().slice(0, 10);

  let best = null;
  for (const r of archive) {
    if (!r || _isoDay(r.date) !== prevKey) continue;
    if (!best || String(r.archivedAt || '') > String(best.archivedAt || '')) best = r;
  }
  if (!best) return null;

  const v = parseFloat(best._todayBal);
  return Number.isFinite(v) ? v : null;
}

/**
 * Какая CRG-сделка получает дельту `ftdSyncDelta` при правке FTD-записи.
 *
 * Связь ЯВНАЯ: человек назначает сделку в модалке выбора (FtdsInfo), и её id
 * ложится в саму запись — поле `crgDealId`. Здесь только чтение этой связи,
 * никакого сопоставления по строкам.
 *
 * WHY так, а не «найти похожую» (жюри 2026-08-08, 🟠 HIGH):
 *   До 2026-08-08 сделки выбирались НЕЧЁТКИМ матчем, и дельта применялась к КАЖДОЙ
 *   подошедшей. Одна FTD-запись двигала счётчик у N сделок вместо одной, а счётчик
 *   умножается на цену сделки — завышение уходило прямо в выручку. Угадывали оба
 *   критерия: по ПЕРВОМУ числу в affiliate (сделка «140/175/236 AT» отдавала только
 *   «140», поэтому цеплялась записями аффа 140 и не цеплялась 175 и 236) и по
 *   ПЕРВОМУ СЛОВУ брокера без учёта регистра (на проде префикс «legion» несут
 *   19 разных брокеров, «swin» — 16, «capitan» — 14).
 *
 *   Замер на боевых данных (778 сделок, 1115 записей): 319 записей матчили больше
 *   одной сделки, накрыто 399 сделок. Смягчение «брать лучший матч по скорингу» не
 *   годилось: из 342 записей с >1 кандидатом у 97 (28%) вверху ничья — там сделка
 *   выбиралась бы произвольно. Реализованного ущерба не нашли (по аудиту за месяц
 *   из 1637 записей в crg-deals лишь 9 пришли с FTD-страницы, все корректные) —
 *   баг был заряжен, но не выстрелил.
 *
 *   Вывод владельца: угадывание убрано из денежного пути совсем. Нечёткий матч жив,
 *   но понижен до подсказки — сортирует кандидатов в модалке (FtdsInfo::findCrgMatches)
 *   и больше не решает, куда писать деньги.
 *
 * Связи нет → `null`, и вызывающий обязан СПРОСИТЬ, а не подставить похожую.
 * Единственный кандидат — не повод: это ровно та эвристика, что давала fan-out.
 * Обратная совместимость: у всех записей до 2026-08-08 поля нет, они попадают
 * в ветку «спросить» — старый формат читается, ничего не ломается.
 *
 * Дата намеренно не проверяется: связь точная по id, и перенос сделки на другую
 * дату её не рвёт.
 *
 * @param {Array<object>} deals     все CRG-сделки (crg-deals)
 * @param {object} ftdEntry         правленная FTD-запись
 * @returns {object|null} назначенная сделка, либо null — спросить у человека
 */
export function selectCrgDealForFtdSync(deals, ftdEntry) {
  const dealId = ftdEntry?.crgDealId;
  if (!dealId) return null;
  return (deals || []).find(d => d.id === dealId) || null;
}
